import { constraintScreenOrientationForPreferredOrientationMode } from '@euler/functions/constrainScreenOrientationIfNeeded';
import { isHubBuild, preferredPresentationMode$ } from '@euler/functions/isHub';
import { isMobileBrowser } from '@euler/functions/responsiveness';
import { useDeviceType } from '@euler/functions/useDeviceType';
import { onErrorIgnore } from '@euler/utils';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import * as Devices from 'expo-device';
import * as ScreenOrientation from 'expo-screen-orientation';
import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { Platform } from 'react-native';
import { BehaviorSubject } from 'rxjs';

export type PresentationMode = 'normal' | 'presentation';

export const PresentationModeContext = createContext<{
  mode$: BehaviorSubject<PresentationMode>;
  deviceType: Devices.DeviceType;
}>(null as any);

export const usePresentationMode = () => {
  const { mode$ } = useContext(PresentationModeContext);
  const [mode] = useBehaviorSubject(mode$);
  return mode;
};

export const useTogglePresentationMode = () => {
  return useCallback(async () => {
    if (isHubBuild() || isMobileBrowser()) return;
    const mode = preferredPresentationMode$.value;
    if (mode === 'normal' || mode == null) {
      preferredPresentationMode$.next('presentation');
    } else {
      preferredPresentationMode$.next('normal');
    }
  }, []);
};

const kDefaultPresentationModeForTablet: PresentationMode = 'normal';

const defaultPresentationMode: (
  deviceType: Devices.DeviceType,
) => PresentationMode = deviceType =>
  isMobileBrowser()
    ? 'normal'
    : isHubBuild()
    ? 'presentation'
    : deviceType === Devices.DeviceType.TABLET
    ? kDefaultPresentationModeForTablet
    : 'normal';

export const PresentationModeProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const deviceType = useDeviceType();
    const [preferredMode] = useBehaviorSubject(preferredPresentationMode$);

    if (preferredMode === undefined) {
      return null;
    }

    return (
      <Content
        preferredMode={isMobileBrowser() ? 'normal' : preferredMode}
        deviceType={deviceType}
      >
        {children}
      </Content>
    );
  },
);

const Content = memo(
  ({
    preferredMode,
    deviceType,
    children,
  }: {
    deviceType: Devices.DeviceType;
    preferredMode: PresentationMode | null;
    children?: ReactNode;
  }) => {
    const mode$ = useCreation(
      () =>
        new BehaviorSubject<PresentationMode>(
          isMobileBrowser()
            ? 'normal'
            : preferredMode ?? defaultPresentationMode(deviceType),
        ),
      [deviceType, preferredMode],
    );

    const value = useMemo(() => ({ mode$, deviceType }), [mode$, deviceType]);

    useEffect(() => {
      if (Platform.OS === 'web') return;
      const sub = mode$.subscribe(mode => {
        if (mode === 'normal') {
          constraintScreenOrientationForPreferredOrientationMode(
            mode,
            false,
          ).catch(onErrorIgnore);
        } else {
          ScreenOrientation.lockAsync(
            ScreenOrientation.OrientationLock.LANDSCAPE_RIGHT,
          ).catch(onErrorIgnore);
        }
      });
      return () => sub.unsubscribe();
    }, [mode$]);

    return (
      <PresentationModeContext.Provider value={value}>
        {children}
      </PresentationModeContext.Provider>
    );
  },
);
